import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { AddressAutofill } from '@mapbox/search-js-react';
import { CREATE_ACCOUNT_CREDIT_CARD } from '@retainerclub/shared-api';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { PatternFormat } from 'react-number-format';
import {
  BaseLoader,
  CheckboxInput,
  CountrySelect,
  ErrorMessage,
  FieldContainer,
  Input,
  RegionSelect,
  SubmitButton,
} from '../../../shared';
import { initialValues, schema } from './helpers';

function AccountCreditCardModalWrapper({
  handleCloseModal,
  accountId = null,
  hideDefaultCardCheckbox = false,
}) {
  const [mutationErrors, setMutationErrors] = useState(null);

  const [create, { error, loading }] = useMutation(CREATE_ACCOUNT_CREDIT_CARD);

  const memoizedInitialValues = useMemo(
    () => initialValues({ accountId }),
    [accountId],
  );

  const {
    register,
    handleSubmit,
    formState: { isLoading, isSubmitting, isValid, errors },
    formState,
    getValues,
    setValue,
    control,
    ...rest
  } = useForm({
    resolver: yupResolver(schema()),
    defaultValues: memoizedInitialValues,
    values: memoizedInitialValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  async function submitRecord(values) {
    try {
      const params = {
        variables: {
          input: {
            input: { ...values },
            clientMutationId: crypto.randomUUID(),
          },
        },
      };

      const { data } = await create(params);

      const mutationResult = data?.createAccountCreditCard;
      const mErrors = mutationResult?.errorMessages || [
        {
          message: 'Unable to create credit card. Please verify the card details and try again.',
          path: 'credit_card',
        },
      ];

      if (mErrors.length > 0) {
        setMutationErrors(mErrors);
      } else {
        setMutationErrors(null);
        handleCloseModal();
      }
    } catch (err) {
      setMutationErrors([
        {
          message:
            err?.message ||
            'Unable to create credit card. Please verify the card details and try again.',
          path: 'credit_card',
        },
      ]);
    }
  }

  if (loading) {
    return <BaseLoader title="Credit Card" />;
  }

  return (
    <FormProvider
      {...rest}
      control={control}
      register={register}
      handleSubmit={handleSubmit}
      formState={formState}
      getValues={getValues}
      setValue={setValue}
    >
      <form onSubmit={handleSubmit(submitRecord)} noValidate>
        <div>
          <div>
            <div>
              <h2 className="font-medium text-gray-900 text-lg">
                Card Details
              </h2>

              <div className="mt-4 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-6">
                <Input
                  className="sm:col-span-2"
                  required
                  name="nameOnCard"
                  label="Cardholder Name"
                  autoComplete="cc-name"
                />
                <Controller
                  name="cardNumber"
                  render={({
                    field: { onChange, onBlur, name, value, ref },
                  }) => (
                    <FieldContainer
                      className="sm:col-span-2"
                      required
                      name="cardNumber"
                      label="Card Number"
                    >
                      <PatternFormat
                        format="#### #### #### ####"
                        name={name}
                        getInputRef={ref}
                        onBlur={onBlur}
                        value={value}
                        onChange={onChange}
                        autoComplete="cc-number"
                        valueIsNumericString
                        className={`block w-full rounded-md border-0 py-1.5 shadow-xs ring-1 ring-inset focus:ring-2 focus:ring-inset disabled:cursor-not-allowed disabled:bg-rcgray-600 disabled:text-rcgray-600 disabled:ring-rcgray-700 sm:text-sm sm:leading-6 ${
                          errors?.[name]
                            ? 'text-red-900 ring-red-500 placeholder:text-red-500 focus:ring-red-500'
                            : 'ring-gray-300 placeholder:text-gray-400 focus:ring-rcprimary-400'
                        }`}
                        inputMode="numeric"
                      />
                    </FieldContainer>
                  )}
                />
                <div className="sm:col-span-2">
                  <img
                    alt="Accepted payment types: Visa, Mastercard, American Express, Discover"
                    src="https://cdn.retainerclub.com/images/payment-icons/brand_logos.png"
                    className="mt-8 max-h-8"
                  />
                </div>

                <Controller
                  name="expirationDate"
                  render={({
                    field: { onChange, onBlur, name, value, ref },
                  }) => (
                    <FieldContainer
                      className="sm:col-span-2"
                      required
                      name="expirationDate"
                      label="Exp. Date"
                    >
                      <PatternFormat
                        format="##/##"
                        name={name}
                        getInputRef={ref}
                        onBlur={onBlur}
                        value={value}
                        onChange={onChange}
                        valueIsNumericString
                        allowEmptyFormatting
                        inputMode="numeric"
                        mask="_"
                        autoComplete="cc-exp"
                        className={`block w-full rounded-md border-0 py-1.5 shadow-xs ring-1 ring-inset focus:ring-2 focus:ring-inset disabled:cursor-not-allowed disabled:bg-rcgray-600 disabled:text-rcgray-600 disabled:ring-rcgray-700 sm:text-sm sm:leading-6 ${
                          errors?.[name]
                            ? 'text-red-900 ring-red-500 placeholder:text-red-500 focus:ring-red-500'
                            : 'ring-gray-300 placeholder:text-gray-400 focus:ring-rcprimary-400'
                        }`}
                      />
                    </FieldContainer>
                  )}
                />

                <Input
                  className="sm:col-span-2"
                  required
                  name="cvv"
                  label="CVV"
                  autoComplete="csc"
                  placeholder="123"
                  attributes={{ maxLength: 4 }}
                  inputMode="numeric"
                />
                {!hideDefaultCardCheckbox && (
                  <div className="sm:col-span-2">
                    <div className="sm:col-span-full">
                      <CheckboxInput
                        name="defaultCard"
                        label="Default?"
                        description="Make this the default credit card?"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-4 border-rcgray-300 border-t pt-4">
                <h2 className="font-medium text-gray-900 text-lg">
                  Billing Address
                </h2>

                <div className="mt-4 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-6">
                  <div className="sm:col-span-6">
                    <AddressAutofill
                      accessToken={import.meta.env.VITE_MAPBOX_ACCESS_TOKEN}
                      confirmOnBrowserAutofill={{
                        minimap: true,
                        skipConfirmModal: (feature) =>
                          ['exact', 'high'].includes(
                            feature.properties.match_code.confidence,
                          ),
                      }}
                      onRetrieve={(address) => {
                        if (address?.features?.length > 0) {
                          const {
                            country_code: countryCode,
                            region_code: regionCode,
                            matching_name: matchingName,
                          } = address.features[0].properties;

                          setValue(
                            'billingAddress.country',
                            countryCode.toUpperCase(),
                          );

                          setValue(
                            'billingAddress.region',
                            regionCode.toUpperCase(),
                          );

                          setValue('billingAddress.address', matchingName);
                        }
                      }}
                      onChange={(event) => {
                        setValue('billingAddress.address', event);
                      }}
                      options={{
                        country: 'US',
                        language: 'en',
                        proximity: 'ip',
                      }}
                    >
                      <Input
                        name="billingAddress.address"
                        label="Address"
                        required
                        autoComplete=""
                      />
                    </AddressAutofill>
                  </div>

                  <div className="sm:col-span-3">
                    <Input
                      name="billingAddress.address2"
                      label="Address Line 2"
                      autoComplete="address-line2"
                    />
                  </div>
                  <div className="sm:col-span-3">
                    <Input
                      name="billingAddress.city"
                      label="City"
                      required
                      autoComplete="address-level2"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <CountrySelect
                      name="billingAddress.country"
                      required
                      autoComplete=""
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <RegionSelect
                      name="billingAddress.region"
                      countryFieldName="billingAddress.country"
                      autoComplete=""
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Input
                      name="billingAddress.postalCode"
                      label="Postal Code"
                      required
                      autoComplete="postal-code"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="col-span-full">
              {error && (
                <div className="col-span-full mt-4">
                  <ErrorMessage error={error} />
                </div>
              )}

              {mutationErrors && (
                <div className="col-span-full mt-4">
                  <ErrorMessage errorMessages={mutationErrors} />
                </div>
              )}
              <div className="col-span-full mt-4 flex items-center justify-end gap-x-4">
                <SubmitButton
                  isLoading={isLoading || loading}
                  isSubmitting={isSubmitting}
                  isValid={isValid}
                  label="Create"
                  full
                  notRounded
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}

AccountCreditCardModalWrapper.propTypes = {
  handleCloseModal: PropTypes.func.isRequired,
  accountCreditCard: PropTypes.object,
  accountId: PropTypes.string,
  hideDefaultCardCheckbox: PropTypes.bool,
};

export default AccountCreditCardModalWrapper;
